import { ActionIcon, Tooltip } from '@mantine/core';
import { Environment, type CameraControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { LoadingAnimation, variants } from '@uag/react-core';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxHelper, type Group } from 'three';
import { useShallow } from 'zustand/shallow';

import { ActionShortcutHint } from 'app/modules/wallforming/components/input-binding/ActionShortcutHint';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { usePersistentPreviewCamera } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/usePersistentPreviewCamera';
import { PreviewCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewCameraControls';
import { PreviewOrientationCube } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewOrientationCube';
import { setFocusOnSolution } from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraUtils';
import { PlacementPreview } from 'app/modules/wallforming/components/scenes/objects/placement-preview/PlacementPreview';
import { SceneLighting } from 'app/modules/wallforming/components/scenes/SceneLighting';
import { ScenePostProcessing } from 'app/modules/wallforming/components/scenes/ScenePostProcessing';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const screenWidth = window.screen.width;

export const PlacementSolutionPreview = () => {
    const { t } = useTranslation();

    const previewCameraControlsRef = useRef<CameraControls | null>(null);
    const previewHelperRef = useRef<BoxHelper | undefined>(undefined);
    const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);

    const { selectedSolution, automatedPlacementTab } = usePlacementStore(
        useShallow((state) => {
            const index = state.selectedPlacementSolution;
            return {
                selectedSolution: index === null ? null : (state.placementSolutions[index] ?? null),
                automatedPlacementTab: state.automatedPlacementTab
            };
        })
    );

    const { shadowsEnabled, reflectionsEnabled } = usePlacementStore(useShallow((state) => state.graphicsSettings));

    const { restorePreviewCamera, savePreviewCamera } = usePersistentPreviewCamera(
        previewCameraControlsRef,
        'wallforming-solution-preview-camera-view',
        selectedSolution ? JSON.stringify(selectedSolution.placements) : null
    );

    const handleFocusSolution = useCallback(() => {
        setFocusOnSolution(previewCameraControlsRef.current, previewHelperRef.current, selectedSolution);
    }, [selectedSolution]);

    const handleOnSetFocus = useCallback(
        (group: Group) => {
            previewHelperRef.current?.geometry.dispose();
            previewHelperRef.current = new BoxHelper(group, 0xffff00);
            if (!restorePreviewCamera()) {
                handleFocusSolution();
            }
        },
        [handleFocusSolution, restorePreviewCamera]
    );

    return (
        <div
            className="relative h-full w-full overflow-hidden border border-border-ultra-light"
            tabIndex={0}
            data-input-scene
            onPointerDown={(event) => {
                event.stopPropagation();
                event.currentTarget.focus();
            }}
        >
            <div className="relative">
                <div className="absolute top-3 left-3 z-50">
                    <Tooltip
                        label={
                            <div className="flex items-center gap-2">
                                <span>{t('buttonFocusSolution')}</span>
                                <ActionShortcutHint actionId={InputActionId.CameraCenter} />
                            </div>
                        }
                        position="right"
                        variant={variants.tooltip.right}
                    >
                        <ActionIcon
                            aria-label={t('buttonFocusSolution')}
                            className={`
                                bg-background-gray-ultra-light hover:bg-background-gray! catalogue-preview-action-icon
                                ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                            `}
                            onClick={handleFocusSolution}
                        >
                            view_in_ar
                        </ActionIcon>
                    </Tooltip>
                </div>
            </div>
            {isLoadingPreview && automatedPlacementTab === PlacementTab.SOLUTIONS && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-background-default/80">
                    <LoadingAnimation text={t('labelLoadSolution')} />
                </div>
            )}

            <Canvas frameloop="demand" shadows={shadowsEnabled ? 'soft' : false} orthographic>
                <PreviewCameraControls
                    previewCameraControlsRef={previewCameraControlsRef}
                    onCenterCamera={handleFocusSolution}
                    onRest={savePreviewCamera}
                />
                <PreviewOrientationCube />
                <PlacementPreview
                    isMainCanvas={false}
                    centered
                    onBoundsReady={handleOnSetFocus}
                    onLoadingChange={setIsLoadingPreview}
                />
                <Environment
                    background={false}
                    environmentIntensity={shadowsEnabled || reflectionsEnabled ? 1.6 : 0}
                    environmentRotation={[Math.PI / 2, -1, 0]}
                    preset="city"
                />
                <SceneLighting />
                <ScenePostProcessing />
            </Canvas>
        </div>
    );
};
